document.addEventListener('DOMContentLoaded', () => {
  initAnnouncementRotator();
  initMobileMenu();
  initHeader();
  initReveals();
  initProductGallery();
  initProductForm();
  initQuantityControls();
  initCollectionSort();
  initQuickAdd();
});

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function initAnnouncementRotator() {
  const rotator = document.querySelector('[data-announcement-rotator]');
  if (!rotator) return;

  const messages = [...rotator.querySelectorAll('[data-announcement-message]')];
  if (messages.length < 2 || prefersReducedMotion()) return;

  const speed = Math.max(Number(rotator.dataset.rotationSpeed) || 5000, 3000);
  let current = 0;
  let timer;

  const showNext = () => {
    messages[current].hidden = true;
    messages[current].classList.remove('is-active');
    current = (current + 1) % messages.length;
    messages[current].hidden = false;
    messages[current].classList.add('is-active');
  };

  const start = () => {
    window.clearInterval(timer);
    timer = window.setInterval(showNext, speed);
  };

  const stop = () => window.clearInterval(timer);
  rotator.addEventListener('mouseenter', stop);
  rotator.addEventListener('mouseleave', start);
  rotator.addEventListener('focusin', stop);
  rotator.addEventListener('focusout', start);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stop();
    else start();
  });
  start();
}

function initMobileMenu() {
  const toggle = document.querySelector('[data-menu-toggle]');
  const menu = document.querySelector('[data-mobile-nav]');
  const close = document.querySelector('[data-menu-close]');
  if (!toggle || !menu) return;

  const closeMenu = (restoreFocus = true) => {
    toggle.setAttribute('aria-expanded', 'false');
    menu.hidden = true;
    document.body.classList.remove('menu-open');
    if (restoreFocus) toggle.focus();
  };

  const openMenu = () => {
    toggle.setAttribute('aria-expanded', 'true');
    menu.hidden = false;
    document.body.classList.add('menu-open');
    const firstLink = menu.querySelector('a');
    if (firstLink) firstLink.focus();
  };

  toggle.addEventListener('click', () => {
    if (toggle.getAttribute('aria-expanded') === 'true') closeMenu();
    else openMenu();
  });
  if (close) close.addEventListener('click', () => closeMenu());
  menu.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', () => closeMenu(false));
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !menu.hidden) closeMenu();
  });
}

function initHeader() {
  const group = document.getElementById('site-header-group');
  const header = document.querySelector('[data-site-header]');
  if (!group || !header) return;

  const updateHeight = () => {
    document.documentElement.style.setProperty('--header-group-height', `${group.offsetHeight}px`);
  };
  const updateState = () => header.classList.toggle('is-scrolled', window.scrollY > 12);

  updateHeight();
  updateState();
  window.addEventListener('resize', updateHeight, { passive: true });
  window.addEventListener('scroll', updateState, { passive: true });
}

function initReveals() {
  const elements = [...document.querySelectorAll('[data-reveal]')];
  if (!elements.length) return;
  if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
    elements.forEach((element) => element.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -30px 0px' });

  elements.forEach((element) => observer.observe(element));
}

function initProductGallery() {
  const gallery = document.querySelector('[data-product-gallery]');
  if (!gallery) return;

  const featured = gallery.querySelector('.product-gallery__featured-image');
  const thumbs = [...gallery.querySelectorAll('[data-product-thumb]')];
  if (!featured || !thumbs.length) return;

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const image = thumb.querySelector('img');
      if (!image) return;
      featured.src = thumb.dataset.imageSrc || image.src;
      featured.srcset = '';
      featured.alt = image.alt;
      thumbs.forEach((item) => item.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });
}

function initProductForm() {
  const form = document.querySelector('.product-form--main');
  const productData = document.querySelector('[data-product-json]');
  if (!form || !productData) return;

  const variantInput = form.querySelector('[name="id"]');
  const selectors = [...form.querySelectorAll('[data-option-select]')];
  const price = document.querySelector('[data-product-price]');
  const compare = document.querySelector('[data-product-compare-price]');
  const button = form.querySelector('[name="add"]');
  if (!variantInput || !button) return;

  let product;
  try {
    product = JSON.parse(productData.textContent);
  } catch (error) {
    return;
  }

  const moneyFormat = document.documentElement.dataset.moneyFormat || '${{amount}}';
  const formatMoney = (cents) => {
    const amount = (cents / 100).toFixed(2);
    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)));
  };

  const update = () => {
    const options = selectors.map((selector) => selector.value);
    const variant = product.variants.find((item) =>
      item.options.every((option, index) => option === options[index])
    );
    if (!variant) {
      button.disabled = true;
      return;
    }

    variantInput.value = variant.id;
    if (price) price.textContent = formatMoney(variant.price);
    if (compare) {
      const onSale = variant.compare_at_price > variant.price;
      compare.hidden = !onSale;
      compare.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
    }
    button.disabled = !variant.available;
    const label = button.querySelector('span');
    if (label) {
      label.textContent = variant.available
        ? button.dataset.availableLabel
        : button.dataset.soldLabel;
    }
  };

  selectors.forEach((selector) => selector.addEventListener('change', update));
}

function initQuantityControls() {
  document.querySelectorAll('[data-quantity]').forEach((control) => {
    const input = control.querySelector('input');
    if (!input) return;
    control.querySelectorAll('[data-quantity-change]').forEach((button) => {
      button.addEventListener('click', () => {
        const delta = Number(button.dataset.quantityChange);
        const minimum = Number(input.min) || 1;
        input.value = Math.max(minimum, (Number(input.value) || minimum) + delta);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  });
}

function initCollectionSort() {
  const select = document.querySelector('[data-collection-sort]');
  if (!select) return;
  select.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    window.location.assign(url.toString());
  });
}

function initQuickAdd() {
  document.querySelectorAll('[data-quick-add-form]').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = form.querySelector('[name="add"]');
      const label = button?.querySelector('span');
      const status = form.querySelector('[data-quick-add-status]');
      if (!button || button.disabled) return;

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (label) label.textContent = 'Adding…';
      if (status) status.textContent = '';

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: new FormData(form)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Unable to add this item.');

        const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await cartResponse.json();
        document.querySelectorAll('[data-cart-count]').forEach((count) => {
          count.textContent = cart.item_count > 0 ? cart.item_count : '';
        });
        document.querySelectorAll('[data-mobile-cart-count]').forEach((count) => {
          count.textContent = cart.item_count;
        });
        if (label) label.textContent = 'Added';
        if (status) status.textContent = `${result.title || 'Item'} added to cart.`;
      } catch (error) {
        if (label) label.textContent = 'Try again';
        if (status) status.textContent = error.message;
      } finally {
        button.removeAttribute('aria-busy');
        window.setTimeout(() => {
          button.disabled = false;
          if (label) label.textContent = button.dataset.defaultLabel || 'Add to cart';
        }, 1800);
      }
    });
  });
}
